import React, { useState, useRef, useCallback, useEffect } from 'react';

import styles from './ImageUploader.module.css';

/**
 * ImageUploader Component
 *
 * Provides drag-and-drop and click-to-select file upload functionality
 * with preview and validation for image files.
 *
 * Features:
 * - Drag-and-drop zone with visual feedback
 * - Click-to-select file upload
 * - Multiple file support
 * - Image preview before upload
 * - File type and size validation
 * - Progress indication during upload
 */

/**
 *
 */
type ImagePreview = {
  readonly file: File;
  readonly preview: string;
};

/**
 *
 */
type ImageUploaderProps = {
  readonly onUpload: (files: ReadonlyArray<File>) => Promise<void>;
  readonly loading?: boolean;
  readonly categoryId?: string;
  readonly maxFiles?: number;
  readonly maxFileSize?: number; // in bytes
  readonly accept?: ReadonlyArray<string>;
};

const ImageUploader: React.FC<ImageUploaderProps> = ({
  onUpload,
  loading = false,
  maxFiles = 50,
  maxFileSize = 10 * 1024 * 1024, // 10MB default
  accept = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'],
}) => {
  const [previews, setPreviews] = useState<ReadonlyArray<ImagePreview>>([]);
  const [isDragActive, setIsDragActive] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Cleanup object URLs on unmount to prevent memory leaks
  useEffect(() => {
    return (): void => {
      previews.forEach((preview) => {
        URL.revokeObjectURL(preview.preview);
      });
    };
  }, [previews]);

  /**
   * Validate files for type and size
   */
  const validateFiles = useCallback(
    (
      files: ReadonlyArray<File>
    ): { readonly valid: ReadonlyArray<File>; readonly errors: ReadonlyArray<string> } => {
      const valid: Array<File> = [];

      const errors: Array<string> = [];

      files.forEach((file) => {
        // Check file type
        if (!accept.includes(file.type)) {
          // eslint-disable-next-line functional/immutable-data
          errors.push(`${file.name}: Invalid file type. Only images allowed.`);
          return;
        }

        // Check file size
        if (file.size > maxFileSize) {
          const sizeMB = (maxFileSize / (1024 * 1024)).toFixed(0);
          // eslint-disable-next-line functional/immutable-data
          errors.push(`${file.name}: File exceeds ${sizeMB}MB limit.`);
          return;
        }

        // eslint-disable-next-line functional/immutable-data
        valid.push(file);
      });

      // Check max files
      if (valid.length + previews.length > maxFiles) {
        // eslint-disable-next-line functional/immutable-data
        errors.push(`Maximum ${maxFiles} files per upload.`);
        return { valid: [], errors };
      }

      return { valid, errors };
    },
    [accept, maxFileSize, previews.length, maxFiles]
  );

  /**
   * Process and add files to preview list
   */
  const addFiles = useCallback(
    (files: ReadonlyArray<File>) => {
      const { valid, errors } = validateFiles(files);

      if (errors.length > 0) {
        setError(errors.join(' '));
        return;
      }

      setError(null);

      // Create preview objects
      const newPreviews: ReadonlyArray<ImagePreview> = valid.map((file) => ({
        file,
        preview: URL.createObjectURL(file),
      }));

      setPreviews((prev) => [...prev, ...newPreviews]);
    },
    [validateFiles]
  );

  /**
   * Handle drag enter
   * @param e
   */
  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(true);
  };

  /**
   * Handle drag leave
   * @param e
   */
  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
  };

  /**
   * Handle drag over
   * @param e
   */
  const handleDragOver = (e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    e.stopPropagation();
  };

  /**
   * Handle file drop
   * @param e
   */
  const handleDrop = (e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);

    const droppedFiles = Array.from(e.dataTransfer.files);
    if (droppedFiles.length > 0) {
      addFiles(droppedFiles);
    }
  };

  /**
   * Handle file input change (click-to-select)
   * @param e
   */
  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const selectedFiles = e.target.files;
    if (selectedFiles && selectedFiles.length > 0) {
      addFiles(Array.from(selectedFiles));
    }
    // Reset input value to allow selecting same files again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  /**
   * Handle click on drop zone (trigger file input)
   */
  const handleClick = (): void => {
    fileInputRef.current?.click();
  };

  /**
   * Remove a preview from the list
   * @param index
   */
  const handleRemovePreview = (index: number): void => {
    setPreviews((prev) => {
      const updated = [...prev];
      // Revoke object URL before removing
      URL.revokeObjectURL(updated[index].preview);
      updated.splice(index, 1);
      return updated;
    });
  };

  /**
   * Handle upload button click
   */
  const handleUpload = async (): Promise<void> => {
    if (previews.length === 0) return;

    try {
      setError(null);
      const files = previews.map((p) => p.file);
      await onUpload(files);

      // Clear previews after successful upload
      previews.forEach((preview) => {
        URL.revokeObjectURL(preview.preview);
      });
      setPreviews([]);
    } catch (err) {
      setError('Upload failed. Please try again.');
      console.error('Upload error:', err);
    }
  };

  /**
   * Handle cancel button click
   */
  const handleCancel = (): void => {
    // Revoke all object URLs
    previews.forEach((preview) => {
      URL.revokeObjectURL(preview.preview);
    });
    setPreviews([]);
    setError(null);
  };

  return (
    <div className={styles.imageUploader}>
      {/* Drop Zone */}
      <div
        className={`${styles.dropZone} ${isDragActive ? styles.dragActive : ''} ${previews.length > 0 ? styles.hasFiles : ''}`}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onClick={handleClick}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={accept.join(',')}
          onChange={handleFileInputChange}
          className={styles.fileInput}
        />

        <div className={styles.dropZoneContent}>
          {isDragActive ? (
            <>
              <span className={styles.icon}>📥</span>
              <p className={styles.primaryText}>Drop files to upload</p>
            </>
          ) : (
            <>
              <span className={styles.icon}>📁</span>
              <p className={styles.primaryText}>Drag & drop images here</p>
              <p className={styles.secondaryText}>or click to select files</p>
              <p className={styles.hint}>
                Max {maxFiles} files, {(maxFileSize / (1024 * 1024)).toFixed(0)}MB each
              </p>
            </>
          )}
        </div>
      </div>

      {/* Error Message */}
      {error ? <div className={styles.errorMessage}>⚠️ {error}</div> : null}

      {/* Preview Area */}
      {previews.length > 0 && (
        <div className={styles.previewArea}>
          <div className={styles.previewHeader}>
            <h3>Selected Images ({previews.length})</h3>
          </div>

          <div className={styles.previewGrid}>
            {previews.map((preview, index) => (
              <div key={index} className={styles.previewItem}>
                <img src={preview.preview} alt={preview.file.name} />
                <button
                  type="button"
                  className={styles.removeButton}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemovePreview(index);
                  }}
                  title="Remove"
                >
                  ✕
                </button>
                <div className={styles.fileName}>{preview.file.name}</div>
              </div>
            ))}
          </div>

          {/* Action Buttons */}
          <div className={styles.actions}>
            <button
              type="button"
              className={styles.uploadButton}
              onClick={handleUpload}
              disabled={loading || previews.length === 0}
            >
              {loading ? (
                <>
                  <span className={styles.spinner}></span>
                  Uploading...
                </>
              ) : (
                `Upload ${previews.length} Image${previews.length > 1 ? 's' : ''}`
              )}
            </button>
            <button
              type="button"
              className={styles.cancelButton}
              onClick={handleCancel}
              disabled={loading}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageUploader;
